import type {
  AddonsPaymentGroup
} from '../components/AddonsPaymentModal/components/AddonsPaymentModal';

export type BillableRecord = {
  recordId: string;
  isPrimary: boolean | null;
  type: string;
  desc: string;
  chargedAs: string | null;
  amount: number;
  status: string;
  teamAllocations?: any;
  createdAt: string;
};

export type BillableSnapshotInput = {
  isPrimary: boolean;
  type: string;
  desc: string;
  chargedAs: string;
  amount: number;
  status: string;
  teamAllocations?: any;
};

// Exported so billable builders (buildBillablesFromLineItems, CostsTab's
// snapshot builders) share these exact tokens instead of hand-typing them —
// `'completed (auto)'` in particular is a compound literal that has been written
// inconsistently before.
export const STATUS_WITHHELD = 'withheld';
const STATUS_APPROVAL_REQUESTED = 'approval requested';
const STATUS_COMPLETED = 'completed';
export const STATUS_COMPLETED_AUTO = 'completed (auto)';
const STATUS_PAID = 'paid';
const STATUS_PAYMENT_IN_PROGRESS = 'payment in progress';
const STATUS_PENDING_DELIVERY = 'pending delivery';
const STATUS_INVOICE = 'invoice';
export const CHARGED_AS_DOLLARS = 'dollars';
export const CHARGED_AS_INVOICE = 'invoice';
const CHARGED_AS_CREDIT = 'credit';

// Production-credit row types. TWO vocabularies coexist in the ledger:
//   - live finance writes use 'video' (master deliverable) and 'iteration'
//     (a Social Iteration funded by subscription credits).
//   - legacy migration stored the platform PROJECT TYPE as the row type,
//     UPPERCASED — VIDEO / ANIMATION / PODCAST / WEBINAR / PROMO. Each is a
//     production deliverable consumed against the subscription, so all count as
//     production credits.
// Matched case-insensitively (see `isProductionCreditRow`). Creative-services
// credits are deliberately NOT in this set — they are a separate display /
// snapshot bucket (`isCreativeServicesCreditRow`); folding them in would
// double-count them as production. Workspace-level 'balance adjustment' rows
// live under a workspace-event key and never reach a project's record set, so
// they are excluded here simply by omission.
const PRODUCTION_CREDIT_TYPES = new Set([
  'video',
  'iteration',
  'animation',
  'podcast',
  'webinar',
  'promo'
]);

const normaliseType = (type?: string | null): string =>
  (type || '').toLowerCase();

type ClassifiableRecord = { type?: string | null; chargedAs?: string | null };

/**
 * A subscription-credit production deliverable — the master video, or a
 * credit-funded Social Iteration. Recognises both the live ('video' /
 * 'iteration') and legacy (VIDEO / ANIMATION / PODCAST / WEBINAR / PROMO)
 * vocabularies, case-insensitively, so migrated projects count the same as
 * natively-billed ones. Single source of truth for the Costs tab's credit
 * display (`deriveFinanceCredits`) and its Shootsta Dollars amend-save snapshot
 * (`buildShootsDollarsBillables`) so the read and write paths cannot diverge.
 */
export function isProductionCreditRow(record: ClassifiableRecord): boolean {
  return (
    record.chargedAs === CHARGED_AS_CREDIT &&
    PRODUCTION_CREDIT_TYPES.has(normaliseType(record.type))
  );
}

/** A creative-services credit row (master output only). */
export function isCreativeServicesCreditRow(record: ClassifiableRecord): boolean {
  return (
    record.chargedAs === CHARGED_AS_CREDIT &&
    normaliseType(record.type) === 'creative services'
  );
}

/**
 * The dollar-priced Social Iteration deliverable — type 'iteration' charged as
 * anything other than credit. Distinct from the credit-funded iteration above:
 * one output can carry BOTH as sibling rows, so `chargedAs` is what separates
 * them, not `type`.
 */
export function isIterationDollarRow(record: ClassifiableRecord): boolean {
  return (
    normaliseType(record.type) === 'iteration' &&
    record.chargedAs !== CHARGED_AS_CREDIT
  );
}

// A row that has been superseded — getFinanceBillables returns the latest record
// per billable, but we guard defensively against deleted/overwritten rows
// leaking through.
export const INACTIVE_BILLABLE_STATUSES = new Set(['deleted', 'overwritten']);

export type FinanceBillableLike = {
  // `boolean | null` covers a live getFinanceBillables BillableRecord directly
  // (its isPrimary is nullable); every read here coerces via Boolean(), so the
  // wider union is safe for existing consumers.
  isPrimary?: boolean | null;
  type?: string | null;
  desc?: string;
  chargedAs?: string | null;
  amount?: number | string;
  status?: string | null;
  teamAllocations?: any;
};

export type DerivedFinanceCredits = {
  prodCredits: number;
  creativeServiceCredits: number;
  iterationCost: number;
};

/**
 * Maps a Finance `getFinanceBillables` record set (the latest active billables
 * for one output / productRecordId) to the credit + iteration values the UI
 * displays. Single source of truth shared by the Costs tab
 * (`useVideoCredits` → `deriveFinanceCredits`) and the common-react
 * PaymentSummary (Overview tab + customer Completed/Review summary), so the read
 * paths cannot diverge — a credit-funded iteration counts the same everywhere.
 * See the classifier comments above for why creative services and workspace
 * adjustments are excluded from production credits.
 */
export function deriveFinanceCredits(
  records?: FinanceBillableLike[] | null
): DerivedFinanceCredits {
  const active = (records || []).filter(
    (r) => !INACTIVE_BILLABLE_STATUSES.has(r.status || '')
  );

  const sumWhere = (pred: (r: FinanceBillableLike) => boolean): number =>
    active.filter(pred).reduce((acc, r) => acc + Number(r.amount || 0), 0);

  return {
    prodCredits: sumWhere(isProductionCreditRow),
    creativeServiceCredits: sumWhere(isCreativeServicesCreditRow),
    // A credit-funded iteration is production credits, not a dollar cost —
    // excluded here so the credit count is never summed into iterationCost.
    iterationCost: sumWhere(isIterationDollarRow)
  };
}

/**
 * Tombstones for credit siblings a rebuilt snapshot no longer carries.
 *
 * `billWorkspace` used to be a pure replace — leaving a row out removed it. It
 * isn't any more: Finance carries a live non-primary CREDIT row that the
 * incoming snapshot says nothing about into the new batch (ADR-0035,
 * shootsta-finance#354). That exists because the emitters which build a payload
 * from platform line items — the customer submit path above all — never modelled
 * Creative Services, and so deleted the client's CS charge on every
 * resubmission, with no ledger event to show for it.
 *
 * The Costs tab is the opposite case: it renders the credit lines and an admin
 * genuinely can zero one. So a removal it intends has to be stated — a
 * `status: 'deleted'` row, the same sentinel used to retire any other line item
 * — or Finance will carry the old charge straight back.
 *
 * Scope mirrors the Finance rule exactly: non-primary credit rows only. The
 * primary is what every emitter models (and is replaced wholesale), and dollar
 * add-ons are still removed by omission.
 */
export function buildCreditRemovalTombstones(
  existingRecords: FinanceBillableLike[] | null | undefined,
  emittedBillables: { type: string }[]
): BillableSnapshotInput[] {
  const emittedTypes = new Set(
    (emittedBillables || []).map((b) => normaliseType(b.type))
  );

  return (existingRecords || [])
    .filter(
      (r) =>
        !r.isPrimary &&
        r.chargedAs === CHARGED_AS_CREDIT &&
        !INACTIVE_BILLABLE_STATUSES.has(r.status || '') &&
        (r.status || '') !== 'deleted' &&
        !emittedTypes.has(normaliseType(r.type))
    )
    .map((r) => ({
      isPrimary: false,
      type: String(r.type),
      desc: String(r.desc || ''),
      chargedAs: CHARGED_AS_CREDIT,
      amount: 0,
      status: 'deleted'
    }));
}

// An additional output (Social Iteration) as the cost summarizer sees it: its
// only relevant field here is the `outputCost` snapshot string (e.g. "SGD $300"),
// which the summarizer prices verbatim. Kept loose so both the TS Costs tab and
// the JS PaymentSummary can pass their existing output objects through unchanged.
export type AdditionalOutputLike = {
  outputId: string;
  outputCost?: string | null;
  [key: string]: any;
};

// Preserve the currency code from the existing snapshot string and re-stamp the
// dollar amount, matching the format the platform writes ("SGD $300"). Falls back
// to the raw amount if no currency code can be recovered (empty / space-less).
// Exported so cost-editing call sites (e.g. CostsTab.handleIterationCostChange)
// re-stamp the same way instead of duplicating the split/format logic.
export const formatOutputCostString = (
  existingOutputCost: string | null | undefined,
  amount: number
): string => {
  const [currencyCode] = (existingOutputCost || '').split(' ');
  return currencyCode ? `${currencyCode} $${amount}` : `$${amount}`;
};

// Per-output iteration-cost override, accepted either as a precomputed
// `{ [outputId]: number }` map or as a `(outputId) => number | undefined` getter.
// The getter form lets a caller pass an existing per-output resolver (e.g.
// CostsTab's `getOutputIterationCost`) directly, without materialising a map.
export type IterationCostLookup =
  | { [outputId: string]: number }
  | ((outputId: string) => number | undefined);

const resolveIterationCostOverride = (
  lookup: IterationCostLookup,
  outputId: string
): number | undefined =>
  typeof lookup === 'function' ? lookup(outputId) : lookup[outputId];

/**
 * Returns a copy of `additionalOutputs` with each output's `outputCost` snapshot
 * string replaced by the Finance-effective iteration dollar cost, when an
 * override is supplied for that outputId. The currency code is preserved from the
 * existing string (e.g. "SGD $300" -> "SGD $0").
 *
 * The project cost summary prices additional outputs from `outputCost` via
 * `getOutputSummaryWithAdditionalOutputs`, a path that never consults Finance —
 * so a credit-funded / invoice-$0 iteration would otherwise keep contributing its
 * stale snapshot value. Repricing here makes the summary total agree with the
 * per-row iteration figure (which already reads Finance via
 * `getEffectiveIterationCost`). Outputs with no override entry are returned
 * unchanged, preserving legacy behaviour off-flag or when Finance is unreachable.
 *
 * `iterationCostByOutputId` may be a precomputed map or a `(outputId) => number |
 * undefined` getter — see `IterationCostLookup`.
 */
export function repriceAdditionalOutputsFromFinance<T extends AdditionalOutputLike>(
  additionalOutputs: T[],
  iterationCostByOutputId: IterationCostLookup
): T[] {
  return (additionalOutputs || []).map((output) => {
    const override = resolveIterationCostOverride(
      iterationCostByOutputId,
      output.outputId
    );
    if (override === undefined) return output;
    return {
      ...output,
      outputCost: formatOutputCostString(output.outputCost, override)
    };
  });
}

/**
 * Resolve the cost to show/charge for a social iteration ($ additional output).
 *
 * Billed iterations: Finance is the source of truth (it captured the rate at
 * bill time). Unbilled iterations: use the org's CURRENT billing-region default
 * — never the `outputCost` snapshot stored on the output, which goes stale the
 * moment the org's billing region changes. Charged work is therefore never
 * re-priced, and unbilled work always reflects the current region.
 *
 * Shared by the Traffic Costs tab and the common-react PaymentSummary so both
 * summary surfaces resolve iteration cost through one policy and cannot drift.
 */
export function getEffectiveIterationCost({
  financeLedgerEnabled,
  hasFinanceRecord,
  financeCost,
  currentRegionDefaultCost
}: {
  financeLedgerEnabled: boolean;
  hasFinanceRecord: boolean;
  financeCost?: number;
  currentRegionDefaultCost: number;
}): number {
  if (financeLedgerEnabled && hasFinanceRecord && financeCost !== undefined) {
    return financeCost;
  }

  return currentRegionDefaultCost;
}

/**
 * Shared invoice-pathway status choice for submission / re-bill snapshots
 * (ADR-0021 "Pending Delivery"). Pre-completion bill-to-org rows land as
 * `pending delivery` so finance's collections queue ignores them until the
 * deliverable is approved; the `triggerFinanceApprovalRequested` extension
 * in hub3api-video then flips them to `invoice` at output approval, the
 * existing entry into finance's collection funnel (ADR-0016).
 *
 * A re-submission against an already-approved output writes `invoice`
 * directly: the trigger doesn't re-fire post-approval, so writing
 * `pending delivery` then would strand the row.
 *
 * Used by both PaymentsInvoice's `buildBillablesFromLineItems` (customer
 * flow) and Traffic CostsTab's `buildFinanceBillablesSnapshot` (admin
 * re-bill / cost amend) so the conditional lives in one place.
 */
export function pickInvoiceStatusForOutput(
  output?: { completedAt?: string | Date | null } | null
): typeof STATUS_PENDING_DELIVERY | typeof STATUS_INVOICE {
  return output && output.completedAt ? STATUS_INVOICE : STATUS_PENDING_DELIVERY;
}

const TERMINAL_OR_IN_PROGRESS_STATUSES = new Set([
  'completed',
  'completed (auto)',
  'approval rejected',
  'paid',
  'payment in progress'
]);

export function isWithheldDollar(record: BillableRecord): boolean {
  return (
    record.chargedAs === CHARGED_AS_DOLLARS && record.status === STATUS_WITHHELD
  );
}

export function isApprovalRequestedDollar(record: BillableRecord): boolean {
  return (
    record.chargedAs === CHARGED_AS_DOLLARS &&
    record.status === STATUS_APPROVAL_REQUESTED
  );
}

export function isPayableDollar(record: BillableRecord): boolean {
  return isWithheldDollar(record) || isApprovalRequestedDollar(record);
}

export function hasPayableDollars(records: BillableRecord[]): boolean {
  return records.some(isPayableDollar);
}

/**
 * True when any record is charged as Shootsta Dollars, regardless of its
 * status (withheld, approval requested, completed, …). The completed-video
 * page uses this to suppress the Stripe "Pay Now" option: once Dollars are
 * involved they are already reserved against the workspace balance, so a
 * card payment on top would double-charge.
 */
export function hasDollarBillables(records: BillableRecord[]): boolean {
  return records.some((r) => r.chargedAs === CHARGED_AS_DOLLARS);
}

// Statuses that mean a line item is fully accounted for — nothing left to
// collect. Covers every settlement method Finance records: dollars deducted
// from the wallet, paid by card/gateway, paid against an invoice, credits
// consumed, and legacy-migrated rows (which import paid dollar add-ons as
// `paid` and consumed credits as `completed`). `refunded` is terminal too.
const SETTLED_STATUSES = new Set([
  'paid',
  'completed',
  'completed (auto)',
  'refunded'
]);

// Statuses that mean money is still owed or an action is pending — these keep
// the project "not settled" so a payment prompt stays available.
const OUTSTANDING_STATUSES = new Set([
  'withheld',
  'approval requested',
  'payment in progress',
  'unpaid',
  'invoice',
  'invoiced'
]);

/**
 * Source-of-truth "is this project paid?" check, driven by Finance record
 * status rather than by inferring a payment method from `chargedAs`.
 *
 * Stripe's payment status only flips to SUCCEEDED for card payments made
 * through the platform gateway; it stays OPEN for everything else (dollars,
 * manual mark-as-paid, and legacy/invoiced settlements). Finance, however,
 * records the real status on each line item — so once Finance is the system
 * of record we read that directly.
 *
 * Returns true when there are real line items (adjustment / allocation /
 * tombstone rows are ignored) and none of them are still outstanding.
 */
export function isProjectSettled(records: BillableRecord[]): boolean {
  const lineItems = records.filter(
    (r) => SETTLED_STATUSES.has(r.status) || OUTSTANDING_STATUSES.has(r.status)
  );
  if (lineItems.length === 0) return false;
  return lineItems.every((r) => SETTLED_STATUSES.has(r.status));
}

export function isAlreadyTerminalOrInProgress(
  records: BillableRecord[]
): boolean {
  return records.some((r) => TERMINAL_OR_IN_PROGRESS_STATUSES.has(r.status));
}

/**
 * Pre-check used before opening Stripe checkout. Returns a blocking status
 * when the project is in a state where calling Stripe again would be wrong
 * (already paid, or already mid-payment). Returns null if checkout can proceed.
 */
export function findCheckoutBlockingStatus(
  records: BillableRecord[]
): typeof STATUS_PAID | typeof STATUS_PAYMENT_IN_PROGRESS | null {
  if (records.some((r) => r.status === STATUS_PAID)) return STATUS_PAID;
  if (records.some((r) => r.status === STATUS_PAYMENT_IN_PROGRESS)) {
    return STATUS_PAYMENT_IN_PROGRESS;
  }
  return null;
}

export function buildSnapshot(
  records: BillableRecord[],
  newDollarStatus: typeof STATUS_COMPLETED | typeof STATUS_APPROVAL_REQUESTED
): BillableSnapshotInput[] {
  return records.map((r) => ({
    isPrimary: Boolean(r.isPrimary),
    type: r.type,
    desc: r.desc,
    chargedAs: r.chargedAs || CHARGED_AS_DOLLARS,
    amount: r.amount,
    status: isPayableDollar(r) ? newDollarStatus : r.status,
    ...(r.teamAllocations !== undefined && r.teamAllocations !== null
      ? { teamAllocations: r.teamAllocations }
      : {})
  }));
}

const TYPE_LABELS: { [key: string]: string } = {
  BURN_IN_CAPTIONS: 'Burn-in Captions',
  SRT_CAPTIONS: 'SRT Captions',
  VOICE_OVER_ARTIST: 'Voice over',
  VOICE_OVER_ARTISTS: 'Voice over',
  VOICE_OVER_ARTISTS_1MIN: 'Voice over (1 min)',
  VOICE_OVER_ARTISTS_2MIN: 'Voice over (2 min)',
  VOICE_OVER_ARTISTS_5MIN: 'Voice over (5 min)',
  VOICE_OVER_ARTIST_MAX: 'Voice over (max)',
  SOCIAL_ITERATION: 'Social iterations',
  SUPERSIZE_MY_EDIT: 'Supersize my edit',
  RUSH_EDIT: 'Rush edit',
  SUPER_RUSH_EDIT: 'Super rush edit',
  EXTRA_FOOTAGE: 'Extra footage',
  LONGFORM_SHORT: 'Longform short',
  LONGFORM_MEDIUM: 'Longform medium',
  LONGFORM_LONG: 'Longform long',
  CHROMA_KEY: 'Chroma key',
  ADDITIONAL_OUTPUT: 'Additional output',
  PRO: 'Production'
};

export function humanizeBillableType(type: string): string {
  if (TYPE_LABELS[type]) return TYPE_LABELS[type];
  return type
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function buildCostBreakdown(
  billablesByOutput: { [outputId: string]: BillableRecord[] },
  currency: string
): { groups: AddonsPaymentGroup[]; totalDue: number } {
  const groupsMap = new Map<string, AddonsPaymentGroup>();
  let totalDue = 0;

  Object.values(billablesByOutput).forEach((records) => {
    records.filter(isPayableDollar).forEach((r) => {
      const key = r.type;
      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          type: key,
          label: humanizeBillableType(key),
          items: []
        });
      }
      groupsMap.get(key)!.items.push({
        desc: r.desc || humanizeBillableType(key),
        amount: r.amount,
        currency
      });
      totalDue += r.amount;
    });
  });

  return {
    groups: Array.from(groupsMap.values()),
    totalDue
  };
}

export type FlatCostRow = {
  key: string;
  label: string;
  amount: number;
  currency: string;
};

/**
 * Flat list of one row per add-on type, aggregating amounts. Retained for
 * callers that want a non-nested view; the modal now uses
 * `buildNestedCostSummary` to match the design (Captions → Burn-in/SRT → items).
 */
export function buildFlatCostRows(
  billablesByOutput: { [outputId: string]: BillableRecord[] },
  currency: string
): { rows: FlatCostRow[]; totalDue: number } {
  const totals = new Map<string, number>();
  let totalDue = 0;

  Object.values(billablesByOutput).forEach((records) => {
    records.filter(isPayableDollar).forEach((r) => {
      totals.set(r.type, (totals.get(r.type) || 0) + r.amount);
      totalDue += r.amount;
    });
  });

  const rows: FlatCostRow[] = Array.from(totals.entries()).map(
    ([type, amount]) => ({
      key: type,
      label: humanizeBillableType(type),
      amount,
      currency
    })
  );

  return { rows, totalDue };
}

export type CostSummaryItem = {
  desc: string;
  amount: number;
  currency: string;
};

export type CostSummarySubGroup = {
  key: string;
  // null when the group has no sub-category; items render directly under
  // the top-level category header.
  label: string | null;
  items: CostSummaryItem[];
};

export type CostSummaryGroup = {
  key: string;
  label: string;
  subGroups: CostSummarySubGroup[];
};

// Maps each billable type to its top-level category and (optional)
// sub-category as shown in the Add-ons Payment modal cost summary.
const TYPE_GROUP_MAP: {
  [type: string]: { group: string; subGroup?: string };
} = {
  BURN_IN_CAPTIONS: { group: 'Captions', subGroup: 'Burn-in' },
  SRT_CAPTIONS: { group: 'Captions', subGroup: 'SRT' },
  VOICE_OVER_ARTIST: { group: 'Voice over' },
  VOICE_OVER_ARTISTS: { group: 'Voice over' },
  VOICE_OVER_ARTISTS_1MIN: { group: 'Voice over' },
  VOICE_OVER_ARTISTS_2MIN: { group: 'Voice over' },
  VOICE_OVER_ARTISTS_5MIN: { group: 'Voice over' },
  VOICE_OVER_ARTIST_MAX: { group: 'Voice over' },
  SOCIAL_ITERATION: { group: 'Social iterations' },
  SUPERSIZE_MY_EDIT: { group: 'Supersize my edit' },
  RUSH_EDIT: { group: 'Rush edit' },
  SUPER_RUSH_EDIT: { group: 'Super rush edit' },
  EXTRA_FOOTAGE: { group: 'Extra footage' },
  CHROMA_KEY: { group: 'Chroma key' },
  ADDITIONAL_OUTPUT: { group: 'Additional output' },
  PRO: { group: 'Production' },
  LONGFORM_SHORT: { group: 'Longform' },
  LONGFORM_MEDIUM: { group: 'Longform' },
  LONGFORM_LONG: { group: 'Longform' }
};

function resolveTypeGroup(type: string): { group: string; subGroup?: string } {
  if (TYPE_GROUP_MAP[type]) return TYPE_GROUP_MAP[type];
  return { group: humanizeBillableType(type) };
}

/**
 * Nested per-design cost summary: top-level categories → optional sub-category
 * groups → individual items. Used by the Add-ons Payment modal.
 */
export function buildNestedCostSummary(
  billablesByOutput: { [outputId: string]: BillableRecord[] },
  currency: string
): { groups: CostSummaryGroup[]; totalDue: number } {
  type GroupAccumulator = {
    label: string;
    subGroups: Map<string, { label: string | null; items: CostSummaryItem[] }>;
  };

  const groupsByKey = new Map<string, GroupAccumulator>();
  let totalDue = 0;

  Object.values(billablesByOutput).forEach((records) => {
    records.filter(isPayableDollar).forEach((r) => {
      const { group, subGroup } = resolveTypeGroup(r.type);
      const subKey = subGroup || '__none__';

      let accumulator = groupsByKey.get(group);
      if (!accumulator) {
        accumulator = { label: group, subGroups: new Map() };
        groupsByKey.set(group, accumulator);
      }
      let bucket = accumulator.subGroups.get(subKey);
      if (!bucket) {
        bucket = { label: subGroup || null, items: [] };
        accumulator.subGroups.set(subKey, bucket);
      }
      bucket.items.push({
        desc: r.desc || humanizeBillableType(r.type),
        amount: r.amount,
        currency
      });
      totalDue += r.amount;
    });
  });

  const groups: CostSummaryGroup[] = Array.from(groupsByKey.entries()).map(
    ([key, value]) => ({
      key,
      label: value.label,
      subGroups: Array.from(value.subGroups.entries()).map(
        ([subKey, subValue]) => ({
          key: `${key}::${subKey}`,
          label: subValue.label,
          items: subValue.items
        })
      )
    })
  );

  return { groups, totalDue };
}

export type PaidByMethod = 'dollars' | 'card' | 'mixed' | 'none';

/**
 * Determines how an already-settled project was paid by inspecting its
 * billables. `completed` ⇒ dollars; `paid` ⇒ Stripe; mixed/other returns 'none'
 * so callers can fall back to the generic "Paid" label.
 */
export function detectPaidByMethod(records: BillableRecord[]): PaidByMethod {
  const dollarRecords = records.filter(
    (r) => r.chargedAs === CHARGED_AS_DOLLARS
  );
  if (dollarRecords.length === 0) return 'none';

  let dollarsPaid = 0;
  let cardPaid = 0;
  let other = 0;
  dollarRecords.forEach((r) => {
    if (r.status === STATUS_COMPLETED) dollarsPaid += 1;
    else if (r.status === STATUS_PAID) cardPaid += 1;
    else other += 1;
  });

  if (other > 0) return 'none';
  if (dollarsPaid > 0 && cardPaid === 0) return 'dollars';
  if (cardPaid > 0 && dollarsPaid === 0) return 'card';
  if (dollarsPaid > 0 && cardPaid > 0) return 'mixed';
  return 'none';
}
